import { clearToken, getToken } from '../auth/token'
import type {
  AgentLink,
  AppConfig,
  AuthResponse,
  Collection,
  DevAuthRequest,
  FavoriteGroup,
  FavoriteGroupDetail,
  FeedCounts,
  FeedCountsParams,
  FeedPage,
  FeedParams,
  Invite,
  PostDetail,
  PostMeasurement,
  Platform,
  PostFavorites,
  Role,
  Source,
  SourceAddRequest,
  SourceAdded,
  SourcePreview,
  SourcePostsParams,
  SourceStateResponse,
  User,
  WebAppAuthRequest,
  WidgetAuthRequest,
} from './types'

export class ApiError extends Error {
  readonly status: number
  /** Строковый detail из ответа FastAPI, если он есть. */
  readonly detail: string | null
  /** 422 от Pydantic: какие поля не прошли (последний элемент `loc`) и почему (`type`). */
  readonly fields: { name: string; type: string }[]
  /** Дополнительные данные ошибки, например число уже отправленных файлов. */
  readonly context: Record<string, unknown> | null

  constructor(status: number, detail: string | null, fields: { name: string; type: string }[] = [], context: Record<string, unknown> | null = null) {
    super(detail ?? `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.fields = fields
    this.context = context
  }
}

/** Список ошибок Pydantic: `[{loc: ['body', 'title'], type: 'string_too_long'}, …]`. */
function validationFields(detail: unknown): { name: string; type: string }[] {
  if (!Array.isArray(detail)) return []
  return detail.flatMap((d: unknown) => {
    if (!d || typeof d !== 'object') return []
    const { loc, type } = d as { loc?: unknown; type?: unknown }
    const name = Array.isArray(loc) ? loc[loc.length - 1] : undefined
    return typeof name === 'string' ? [{ name, type: typeof type === 'string' ? type : '' }] : []
  })
}

// 401 на запросе с токеном значит, что сессия кончилась: токен стираем и сообщаем AuthProvider.
const unauthorizedListeners = new Set<() => void>()

export function onUnauthorized(cb: () => void): () => void {
  unauthorizedListeners.add(cb)
  return () => {
    unauthorizedListeners.delete(cb)
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

export async function request<T>(path: string, { method = 'GET', body, signal }: RequestOptions = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { Accept: 'application/json' }
  const multipart = body instanceof FormData
  if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers,
      body: multipart ? body : body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'network')
  }

  if (res.status === 204) return undefined as T

  const data: unknown = await res.json().catch(() => null)

  if (!res.ok) {
    const rawDetail = data && typeof data === 'object' && 'detail' in data ? data.detail : null
    const context = rawDetail && typeof rawDetail === 'object' && !Array.isArray(rawDetail)
      ? rawDetail as Record<string, unknown> : null
    const detail = typeof rawDetail === 'string' ? rawDetail : typeof context?.code === 'string' ? context.code : null
    if (res.status === 401 && token) {
      clearToken()
      unauthorizedListeners.forEach((cb) => cb())
    }
    const fields = data && typeof data === 'object' && 'detail' in data ? validationFields(data.detail) : []
    throw new ApiError(res.status, detail, fields, context)
  }

  return data as T
}

export const api = {
  config: (signal?: AbortSignal) => request<AppConfig>('/api/config', { signal }),

  authWebApp: (body: WebAppAuthRequest) => request<AuthResponse>('/api/auth/webapp', { method: 'POST', body }),
  authWidget: (body: WidgetAuthRequest) => request<AuthResponse>('/api/auth/widget', { method: 'POST', body }),
  authDev: (body: DevAuthRequest) => request<AuthResponse>('/api/auth/dev', { method: 'POST', body }),

  me: (signal?: AbortSignal) => request<User>('/api/me', { signal }),
  team: (signal?: AbortSignal) => request<User[]>('/api/team', { signal }),
  createInvite: () => request<Invite>('/api/team/invites', { method: 'POST' }),
  removeMember: (telegramId: number) => request<void>(`/api/team/${telegramId}`, { method: 'DELETE' }),
  setRole: (telegramId: number, role: Role) =>
    request<User>(`/api/team/${telegramId}`, { method: 'PATCH', body: { role } }),

  feed: (params: FeedParams & { cursor?: string }, signal?: AbortSignal) =>
    request<FeedPage>(`/api/feed?${toQuery(params)}`, { signal }),
  feedCounts: (params: FeedCountsParams, signal?: AbortSignal) =>
    request<FeedCounts>(`/api/feed/counts?${toQuery(params)}`, { signal }),
  /** Убрать из «Добавленных»: сам пост остаётся в базе. */
  removeManual: (postId: number) => request<void>(`/api/feed/manual/${postId}`, { method: 'DELETE' }),
  post: (id: number, signal?: AbortSignal, measurement: PostMeasurement = {}) =>
    request<PostDetail>(`/api/posts/${id}${Object.keys(measurement).length ? '?' + toQuery(measurement) : ''}`, { signal }),
  sendPost: (id: number) => request<{ sent: 'sent' | 'link' }>(`/api/posts/${id}/send`, { method: 'POST' }),
  sources: (params: { include_removed?: boolean }, signal?: AbortSignal) =>
    request<Source[]>(`/api/sources?${toQuery(params)}`, { signal }),
  source: (id: number, signal?: AbortSignal) => request<Source>(`/api/sources/${id}`, { signal }),
  sourcePosts: (id: number, params: SourcePostsParams & { cursor?: string }, signal?: AbortSignal) =>
    request<FeedPage>(`/api/sources/${id}/posts?${toQuery(params)}`, { signal }),
  sourcePreview: (params: { q: string; platform?: Platform }, signal?: AbortSignal) =>
    request<SourcePreview>(`/api/sources/preview?${toQuery(params)}`, { signal }),
  addSource: (body: SourceAddRequest) => request<SourceAdded>('/api/sources', { method: 'POST', body }),
  pauseSource: (id: number) => request<SourceStateResponse>(`/api/sources/${id}/pause`, { method: 'POST' }),
  resumeSource: (id: number) => request<SourceStateResponse>(`/api/sources/${id}/resume`, { method: 'POST' }),
  removeSource: (id: number) => request<void>(`/api/sources/${id}`, { method: 'DELETE' }),
  collections: (signal?: AbortSignal) => request<Collection[]>('/api/collections', { signal }),
  createCollection: (name: string) => request<Collection>('/api/collections', { method: 'POST', body: { name } }),
  updateCollection: (id: number, body: { name?: string; position?: number }) =>
    request<Collection>(`/api/collections/${id}`, { method: 'PATCH', body }),
  deleteCollection: (id: number) => request<void>(`/api/collections/${id}`, { method: 'DELETE' }),
  setCollectionSources: (id: number, sourceIds: number[]) =>
    request<Collection>(`/api/collections/${id}/sources`, { method: 'PUT', body: { source_ids: sourceIds } }),

  favorites: (signal?: AbortSignal) => request<FavoriteGroup[]>('/api/favorites', { signal }),
  favorite: (id: number, signal?: AbortSignal) => request<FavoriteGroupDetail>(`/api/favorites/${id}`, { signal }),
  createFavorite: (name: string) => request<FavoriteGroup>('/api/favorites', { method: 'POST', body: { name } }),
  renameFavorite: (id: number, name: string) =>
    request<FavoriteGroup>(`/api/favorites/${id}`, { method: 'PATCH', body: { name } }),
  deleteFavorite: (id: number) => request<void>(`/api/favorites/${id}`, { method: 'DELETE' }),
  addToFavorite: (groupId: number, postId: number) =>
    request<void>(`/api/favorites/${groupId}/posts/${postId}`, { method: 'PUT' }),
  removeFromFavorite: (groupId: number, postId: number) =>
    request<void>(`/api/favorites/${groupId}/posts/${postId}`, { method: 'DELETE' }),
  postFavorites: (postId: number, signal?: AbortSignal) =>
    request<PostFavorites>(`/api/posts/${postId}/favorites`, { signal }),
  createAgentLink: (groupId: number) => request<AgentLink>(`/api/favorites/${groupId}/agent-link`, { method: 'POST' }),
  revokeAgentLink: (groupId: number) => request<void>(`/api/favorites/${groupId}/agent-link`, { method: 'DELETE' }),
}

function toQuery(params: object): string {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') q.set(key, String(value))
  }
  return q.toString()
}

export function isApiError(err: unknown, status?: number): err is ApiError {
  return err instanceof ApiError && (status === undefined || err.status === status)
}
